import AdminTable from '../../../components/admin/AdminTable';
import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { History, Plus, Notebook } from 'lucide-react';
import { fetchPagos, Pago } from '../../../services/pagosService';
import AddPagos from '../../../components/pagos/AddPagos';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const PagosPage: React.FC = () => {
    const [pagos, setPagos] = useState<Pago[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState<'add' | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('pago', userLevelId); // Ajusta el modelo según tu backend

    const loadPagos = useCallback(async () => {
        if (!perm?.can_read) {
            setPagos([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchPagos();
            // Orden descendente por ID (más reciente primero)
            const sorted = [...data].sort((a, b) => b.id - a.id);
            setPagos(sorted);
        } catch (error) {
            console.error('Error cargando pagos:', error);
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadPagos();
        }
    }, [loadPagos, perm]);

    // Paginación
    const totalPages = Math.ceil(pagos.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPagos = pagos.slice(startIndex, startIndex + itemsPerPage);

    // Manejadores con actualización optimista
    const handlePagoCreated = (newPago: Pago) => {
        setPagos(prev => [newPago, ...prev]); // Nuevo al inicio
        // Opcional: reordenar si es necesario
        setModalState(null);
    };

    const formatNumber = (value: any): string => {
        if (value === null || value === undefined) return '-';
        const num = typeof value === 'number' ? value : parseFloat(value);
        return isNaN(num) ? '-' : num.toFixed(2);
    };

    if (perm === null) return <Loading text="Cargando pagos..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Pagos</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/pagos" className="text-[#c1348b] hover:text-[#8f1f64]">Pagos</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        onClick={loadPagos}
                        disabled={loading}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        title="Refrescar lista"
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            onClick={() => setModalState('add')}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Pago
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {/* Los pagos son historial append-only: solo se admite alta. */}
                <AddPagos
                    isOpen={modalState === 'add'}
                    onClose={() => setModalState(null)}
                    onCreated={handlePagoCreated}
                />

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cita ID</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Pago</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto (Bs.)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efectivo Rec.</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cambio</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Pago</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando pagos...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayedPagos.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay pagos registrados
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega un nuevo pago para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedPagos.map((pago) => (
                                    <tr key={pago.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {pago.cita_info?.codigo || pago.cita || '-'}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {pago.tipo_pago_nombre || pago.tipo_pago}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{formatNumber(pago.monto)}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{formatNumber(pago.efectivo_recibido)}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{formatNumber(pago.cambio)}</td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {pago.fecha_pago ? new Date(pago.fecha_pago).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">{pago.codigo}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {perm.can_read && !loading && pagos.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={pagos.length}
                    />
                )}
            </div>
        </div>
    );
};

export default PagosPage;

